import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  formatSyncRunStatus,
  getApiBaseUrl,
  getEbaySyncRuns,
} from "../../services/ebaySync";
import { EBAY_SYNC, EBAY_SYNC_DETAIL } from "../../routes/routes";
import "./ebay-sync.css";

const SYNC_RUNS_PAGE_SIZE = 20;

function getPageNumbers(current, total) {
  const boundaryCount = 2;
  const siblingCount = 1;

  if (total <= boundaryCount * 2 + siblingCount * 2 + 3) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const startPages = Array.from({ length: boundaryCount }, (_, i) => i + 1);
  const endPages = Array.from(
    { length: boundaryCount },
    (_, i) => total - boundaryCount + i + 1
  );

  const leftSibling = Math.max(current - siblingCount, boundaryCount + 1);
  const rightSibling = Math.min(current + siblingCount, total - boundaryCount);

  const showLeftEllipsis = leftSibling > boundaryCount + 2;
  const showRightEllipsis = rightSibling < total - boundaryCount - 1;

  const pages = [...startPages];

  if (showLeftEllipsis) {
    pages.push("start-ellipsis");
  } else if (leftSibling === boundaryCount + 2) {
    pages.push(boundaryCount + 1);
  }

  for (let i = leftSibling; i <= rightSibling; i++) {
    if (!pages.includes(i)) pages.push(i);
  }

  if (showRightEllipsis) {
    pages.push("end-ellipsis");
  } else if (rightSibling === total - boundaryCount - 1) {
    pages.push(total - boundaryCount);
  }

  pages.push(...endPages);

  return pages;
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function statusBadgeClass(status) {
  return `ebay-sync__badge ebay-sync__badge--${status || "unknown"}`;
}

function EbaySyncTasks() {
  const apiBaseUrl = getApiBaseUrl();
  const [runs, setRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [count, setCount] = useState(0);

  const fetchRuns = useCallback(
    async (page = 1) => {
      try {
        setIsLoading(true);
        setError("");
        const data = await getEbaySyncRuns(page, SYNC_RUNS_PAGE_SIZE, apiBaseUrl);
        setRuns(Array.isArray(data.results) ? data.results : []);
        setCount(data.count || 0);
        setNextPage(data.next);
        setPrevPage(data.previous);
        setCurrentPage(page);
      } catch (err) {
        setError(err?.message || "Failed to load sync tasks");
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl]
  );

  useEffect(() => {
    fetchRuns(1);
  }, [fetchRuns]);

  return (
    <div className="ebay-sync">
      <div className="ebay-sync__header">
        <button
          type="button"
          className="ebay-sync__pagination-btn"
          onClick={() => fetchRuns(currentPage)}
          disabled={isLoading}
        >
          Refresh
        </button>
      </div>

      {error && <div className="ebay-sync__error">{error}</div>}
      {isLoading && runs.length === 0 && (
        <div className="ebay-sync__loading">Loading sync tasks...</div>
      )}

      {!isLoading && runs.length === 0 && !error && (
        <div className="ebay-sync__loading">No eBay sync tasks yet.</div>
      )}

      {runs.length > 0 && (
        <>
          <div className="ebay-sync__table-wrap">
            <table className="ebay-sync__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Batch</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const counts = run.counts || {};
                  const done = counts.done ?? (counts.listed || 0) + (counts.failed || 0);
                  const total = counts.total || run.summary?.requested || 0;

                  return (
                    <tr key={run.id}>
                      <td>#{run.id}</td>
                      <td>{run.batch_name || run.batch_id || "—"}</td>
                      <td>
                        <span className={statusBadgeClass(run.status)}>
                          {formatSyncRunStatus(run.status)}
                        </span>
                      </td>
                      <td>
                        {done} / {total}
                        {run.summary?.failed > 0 && (
                          <span style={{ color: "#b91c1c", marginLeft: "0.35rem" }}>
                            ({run.summary.failed} failed)
                          </span>
                        )}
                      </td>
                      <td>{formatDate(run.started_at)}</td>
                      <td>{formatDate(run.completed_at)}</td>
                      <td>
                        <Link
                          className="ebay-sync__link"
                          to={EBAY_SYNC_DETAIL.replace(":syncRunId", run.id)}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ebay-sync__pagination">
            <button
              type="button"
              className="ebay-sync__pagination-arrow"
              disabled={!prevPage || isLoading}
              onClick={() => fetchRuns(Math.max(1, currentPage - 1))}
              aria-label="Previous page"
              title="Previous page"
            >
              <ChevronLeft size={15} />
            </button>

            {getPageNumbers(
              currentPage,
              Math.ceil(count / SYNC_RUNS_PAGE_SIZE)
            ).map((page, idx) =>
              typeof page === "number" ? (
                <button
                  key={page}
                  type="button"
                  className={`ebay-sync__page-button${
                    page === currentPage ? " ebay-sync__page-button--active" : ""
                  }`}
                  disabled={isLoading}
                  onClick={() => page !== currentPage && fetchRuns(page)}
                >
                  {page}
                </button>
              ) : (
                <span key={`${page}-${idx}`} className="ebay-sync__page-ellipsis">
                  ...
                </span>
              )
            )}

            <button
              type="button"
              className="ebay-sync__pagination-arrow"
              disabled={!nextPage || isLoading}
              onClick={() => fetchRuns(currentPage + 1)}
              aria-label="Next page"
              title="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </>
      )}

      <p style={{ marginTop: "1.5rem", fontSize: "0.875rem", color: "#6b7280" }}>
        Start a sync from a batch page or the dashboard, then track progress here.
      </p>
    </div>
  );
}

export default EbaySyncTasks;
